import { useCallback, useMemo, useState } from 'react';
import { CountryCode, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { detectNanpCountry } from 'components/CallScribe/Home/areaCodeCountry';
import { clampNational, readPastedNumber } from 'components/CallScribe/Home/dialGuards';

export interface IDialNumber {
  country: CountryCode;
  national: string;
  applyRaw: (raw: string) => void;
  chooseCountry: (country: CountryCode) => void;
  setDefaultCountry: (country: CountryCode) => void;
  applyNumber: (e164: string) => void;
}

const effectiveCountry = (base: CountryCode, national: string): CountryCode =>
  getCountryCallingCode(base) === '1' ? detectNanpCountry(national) ?? base : base;

export const useDialNumber = (
  initialCountry: CountryCode,
  onManualCountry?: () => void,
): IDialNumber => {
  const [base, setBase] = useState<CountryCode>(initialCountry);
  const [national, setNational] = useState('');
  const country = useMemo(() => effectiveCountry(base, national), [base, national]);

  const applyRaw = useCallback((raw: string) => {
    if (/[+a-z]/i.test(raw)) {
      const pasted = readPastedNumber(raw, country);
      if (pasted) {
        if (pasted.country !== country) onManualCountry?.();
        setBase(pasted.country);
        setNational(clampNational(pasted.national, pasted.country));
        return;
      }
    }
    setNational(clampNational(raw, country));
  }, [country, onManualCountry]);

  const chooseCountry = useCallback((next: CountryCode) => {
    onManualCountry?.();
    setBase(next);
    setNational(n => clampNational(n, next));
  }, [onManualCountry]);

  const setDefaultCountry = useCallback((next: CountryCode) => { setBase(next); }, []);

  const applyNumber = useCallback((e164: string) => {
    const parsed = parsePhoneNumberFromString(e164);
    if (!parsed?.country) return;
    setBase(parsed.country);
    setNational(clampNational(String(parsed.nationalNumber), parsed.country));
  }, []);

  return { country, national, applyRaw, chooseCountry, setDefaultCountry, applyNumber };
};
